"use client"

import { Shield, Sword, Users, Zap, Globe, Cpu } from "lucide-react"
import { motion } from "framer-motion"

const features = [
    {
        name: "High Rate PvP",
        description: "Experience massive battles with perfectly tuned x1000 rates for immediate action.",
        icon: Sword,
        color: "text-blue-500",
        bg: "bg-blue-500/10"
    },
    {
        name: "Supreme Stability",
        description: "Custom enterprise-grade DDoS protection ensures your sieges never lag or disconnect.",
        icon: Shield,
        color: "text-emerald-500",
        bg: "bg-emerald-500/10"
    },
    {
        name: "Global Reach",
        description: "Join a diverse community from over 50 countries with dedicated 24/7 support.",
        icon: Globe,
        color: "text-purple-500",
        bg: "bg-purple-500/10"
    },
    {
        name: "Low Latency",
        description: "Strategic server locations in Europe and NA for the fastest response times.",
        icon: Zap,
        color: "text-amber-500",
        bg: "bg-amber-500/10"
    },
    {
        name: "Custom Engine",
        description: "Modified game client with modern optimizations, HD textures, and anti-cheat.",
        icon: Cpu,
        color: "text-red-500",
        bg: "bg-red-500/10"
    },
    {
        name: "Active Staff",
        description: "Managed by professionals with over 10 years of experience in the L2 scene.",
        icon: Users,
        color: "text-cyan-500",
        bg: "bg-cyan-500/10"
    },
]

export function Features() {
    return (
        <section className="py-32 relative overflow-hidden">
            {/* Background elements */}
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-600/5 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-purple-600/5 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2" />

            <div className="container px-4 mx-auto relative z-10">
                <div className="text-center mb-24">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        className="inline-flex items-center gap-2 px-3 py-1 bg-slate-800 border border-slate-700 rounded-full text-slate-400 text-[10px] font-black uppercase tracking-[0.2em] mb-4"
                    >
                        The DragonL2 Edge
                    </motion.div>
                    <h2 className="text-5xl font-black text-white tracking-tighter sm:text-7xl mb-6 uppercase italic">
                        Forged for <span className="text-blue-500">Victory</span>
                    </h2>
                    <p className="text-slate-400 text-xl font-medium max-w-2xl mx-auto italic leading-relaxed">
                        We don't just host servers; we create legends. Discover the features that make us the #1 choice for competitive Lineage 2.
                    </p>
                </div>

                <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
                    {features.map((feature, idx) => (
                        <motion.div
                            key={feature.name}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.1 }}
                            className="group relative p-8 bg-slate-900/40 border border-slate-800/50 rounded-3xl hover:bg-slate-800/50 hover:border-blue-500/30 transition-all hover:shadow-2xl hover:shadow-blue-500/5"
                        >
                            <div className={`w-14 h-14 ${feature.bg} ${feature.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500`}>
                                <feature.icon className="w-8 h-8" />
                            </div>
                            <h3 className="text-2xl font-black text-white mb-3 tracking-tight group-hover:text-white transition-colors uppercase">
                                {feature.name}
                            </h3>
                            <p className="text-slate-400 text-base leading-relaxed font-medium line-clamp-3 italic">
                                {feature.description}
                            </p>

                            {/* Decorative element */}
                            <div className="absolute top-4 right-4 text-slate-800 font-black text-4xl opacity-20 italic">
                                0{idx + 1}
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    )
}
